import type { ParsedFileContentJson } from '@/lib/fileParsing';
import type {
  CampaignBlueprintContent,
  CampaignBlueprintConversionPath,
  CampaignBlueprintMeasurement,
  CampaignBlueprintMetric,
  CampaignBlueprintPromotionContent,
  CampaignBlueprintSection,
  CampaignBlueprintTargetAudience,
  CreationCampaignBlueprint,
} from '@/types/creation';
import type { SiteCampaignBlueprint } from '@/types/site';

export const CAMPAIGN_BLUEPRINT_TASK_CODE = 'SITE_CAMPAIGN_BLUEPRINT_GENERATE';
const DEFAULT_BLUEPRINT_SCHEMA_VERSION = 1;

const textValue = (value: unknown) => (typeof value === 'string' ? value.trim() : '');

const numberValue = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const recordValue = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const getTextList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') return item.trim();
        if (item && typeof item === 'object') {
          const record = item as Record<string, unknown>;
          return (
            textValue(record.text) ||
            textValue(record.label) ||
            textValue(record.value) ||
            textValue(record.name)
          );
        }
        return '';
      })
      .filter(Boolean);
  }

  if (typeof value === 'string') {
    return value
      .split(/[,\n]/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const joinTextList = (value: unknown, delimiter = ', ') => {
  const items = getTextList(value);
  return items.length > 0 ? items.join(delimiter) : '';
};

const toDisplayLabel = (value: string) =>
  value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const stripCodeFence = (value: string) =>
  value
    .trim()
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/\s*```$/i, '')
    .trim();

const parseJsonLike = (value: string): unknown => {
  const cleaned = stripCodeFence(value);
  if (!cleaned) return null;

  const attempts = [cleaned];
  const firstBrace = cleaned.indexOf('{');
  const lastBrace = cleaned.lastIndexOf('}');
  if (firstBrace >= 0 && lastBrace > firstBrace) {
    attempts.push(cleaned.slice(firstBrace, lastBrace + 1));
  }
  const firstBracket = cleaned.indexOf('[');
  const lastBracket = cleaned.lastIndexOf(']');
  if (firstBracket >= 0 && lastBracket > firstBracket) {
    attempts.push(cleaned.slice(firstBracket, lastBracket + 1));
  }

  for (const attempt of attempts) {
    try {
      return JSON.parse(attempt);
    } catch {
      continue;
    }
  }

  return null;
};

const normalizeMetrics = (value: unknown): CampaignBlueprintMetric[] => {
  if (Array.isArray(value)) {
    const normalized = value
      .map((item) => {
        if (typeof item === 'string') {
          const [label, ...rest] = item.split(':');
          return rest.length > 0
            ? { label: label.trim(), value: rest.join(':').trim() }
            : { label: 'Detail', value: item.trim() };
        }

        if (!item || typeof item !== 'object') return null;
        const record = item as Record<string, unknown>;
        const label =
          textValue(record.label) ||
          textValue(record.name) ||
          textValue(record.key) ||
          textValue(record.title);
        const metricValue =
          textValue(record.value) ||
          textValue(record.text) ||
          textValue(record.summary) ||
          textValue(record.description);

        if (!label || !metricValue) return null;
        return { label, value: metricValue };
      })
      .filter((item): item is CampaignBlueprintMetric => Boolean(item?.label && item.value));

    if (normalized.length > 0) return normalized;
  }

  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const entries = Object.entries(record)
      .map(([key, entryValue]) => {
        const metricValue = textValue(entryValue);
        if (!metricValue) return null;
        return {
          label: toDisplayLabel(key),
          value: metricValue,
        };
      })
      .filter((item): item is CampaignBlueprintMetric => Boolean(item?.label && item.value));

    if (entries.length > 0) return entries;
  }

  return [];
};

const normalizeSections = (value: unknown): CampaignBlueprintSection[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (typeof item === 'string') {
        const [title, ...rest] = item.split(':');
        return rest.length > 0
          ? { title: title.trim(), value: rest.join(':').trim() }
          : { title: 'Detail', value: item.trim() };
      }

      if (!item || typeof item !== 'object') return null;
      const record = item as Record<string, unknown>;
      const title = textValue(record.title) || textValue(record.label) || textValue(record.heading);
      const sectionValue =
        textValue(record.value) ||
        textValue(record.text) ||
        textValue(record.summary) ||
        textValue(record.description) ||
        textValue(record.body);

      if (!title || !sectionValue) return null;
      return { title, value: sectionValue };
    })
    .filter((item): item is CampaignBlueprintSection => Boolean(item?.title && item.value));
};

const buildFallbackMetrics = (record: Record<string, unknown>) => {
  const fallback = [
    ['Angle', record.angle ?? record.positioningAngle ?? record.directionAngle],
    ['Primary CTA', record.primaryCta ?? record.primaryCTA ?? record.cta],
    ['Hero proof', record.heroProof ?? record.proof ?? record.heroSupport],
  ]
    .map(([label, rawValue]) => {
      const value = textValue(rawValue);
      return value ? { label, value } : null;
    })
    .filter((item): item is CampaignBlueprintMetric => Boolean(item));

  return fallback;
};

const buildSectionValue = (entries: Array<[string, unknown]>) =>
  entries
    .map(([label, rawValue]) => {
      const value =
        typeof rawValue === 'string'
          ? textValue(rawValue)
          : Array.isArray(rawValue)
            ? joinTextList(rawValue)
            : textValue(rawValue);
      return value ? `${label}: ${value}` : '';
    })
    .filter(Boolean)
    .join('\n');

const normalizeTargetAudience = (value: unknown): CampaignBlueprintTargetAudience | undefined => {
  const record = recordValue(value);
  const segments = getTextList(record.segments);
  const painPoints = getTextList(record.painPoints);
  const buyingTrigger = textValue(record.buyingTrigger) || undefined;

  if (segments.length === 0 && painPoints.length === 0 && !buyingTrigger) {
    return undefined;
  }

  return {
    segments,
    painPoints,
    buyingTrigger,
  };
};

const normalizePromotionContent = (
  value: unknown,
): CampaignBlueprintPromotionContent | undefined => {
  const record = recordValue(value);
  const coreMessage = textValue(record.coreMessage) || undefined;
  const landingPageAngle = textValue(record.landingPageAngle) || undefined;
  const googleAdsThemes = getTextList(record.googleAdsThemes);

  if (!coreMessage && !landingPageAngle && googleAdsThemes.length === 0) {
    return undefined;
  }

  return {
    coreMessage,
    landingPageAngle,
    googleAdsThemes,
  };
};

const normalizeConversionPath = (value: unknown): CampaignBlueprintConversionPath | undefined => {
  const record = recordValue(value);
  const landingPageCta = textValue(record.landingPageCta) || undefined;
  const leadMagnet = textValue(record.leadMagnet) || undefined;
  const followUp = textValue(record.followUp) || undefined;

  if (!landingPageCta && !leadMagnet && !followUp) {
    return undefined;
  }

  return {
    landingPageCta,
    leadMagnet,
    followUp,
  };
};

const normalizeMeasurement = (value: unknown): CampaignBlueprintMeasurement | undefined => {
  const record = recordValue(value);
  const primaryEvent = textValue(record.primaryEvent) || undefined;
  const secondaryEvents = getTextList(record.secondaryEvents);
  const optimizationSignal = textValue(record.optimizationSignal) || undefined;

  if (!primaryEvent && secondaryEvents.length === 0 && !optimizationSignal) {
    return undefined;
  }

  return {
    primaryEvent,
    secondaryEvents,
    optimizationSignal,
  };
};

const buildSchemaMetrics = (record: Record<string, unknown>): CampaignBlueprintMetric[] => {
  const conversionPath = normalizeConversionPath(record.conversionPath);
  const measurement = normalizeMeasurement(record.measurement);

  return [
    ['Primary goal', record.primaryGoal],
    ['Audience', record.audience],
    ['Offer', record.offer],
    ['Landing page CTA', conversionPath?.landingPageCta],
    ['Primary event', measurement?.primaryEvent],
  ]
    .map(([label, rawValue]) => {
      const value = textValue(rawValue);
      return value ? { label, value } : null;
    })
    .filter((item): item is CampaignBlueprintMetric => Boolean(item));
};

const buildSchemaTags = (record: Record<string, unknown>) => {
  const promotionContent = normalizePromotionContent(record.promotionContent);
  const combined = [
    ...getTextList(record.channelMix),
    ...(promotionContent?.googleAdsThemes || []),
  ].filter(Boolean);

  return Array.from(new Set(combined)).slice(0, 6);
};

const buildSchemaSections = (record: Record<string, unknown>): CampaignBlueprintSection[] => {
  const targetAudience = normalizeTargetAudience(record.targetAudience);
  const promotionContent = normalizePromotionContent(record.promotionContent);
  const conversionPath = normalizeConversionPath(record.conversionPath);
  const measurement = normalizeMeasurement(record.measurement);
  const assumptions = getTextList(record.assumptions);

  const sections = [
    {
      title: 'Campaign setup',
      value: buildSectionValue([
        ['Primary goal', record.primaryGoal],
        ['Audience', record.audience],
        ['Offer', record.offer],
        ['Channel mix', getTextList(record.channelMix)],
      ]),
    },
    {
      title: 'Target audience',
      value: buildSectionValue([
        ['Segments', targetAudience?.segments || []],
        ['Pain points', targetAudience?.painPoints || []],
        ['Buying trigger', targetAudience?.buyingTrigger],
      ]),
    },
    {
      title: 'Promotion content',
      value: buildSectionValue([
        ['Core message', promotionContent?.coreMessage],
        ['Landing page angle', promotionContent?.landingPageAngle],
        ['Google Ads themes', promotionContent?.googleAdsThemes || []],
      ]),
    },
    {
      title: 'Conversion path',
      value: buildSectionValue([
        ['Landing page CTA', conversionPath?.landingPageCta],
        ['Lead magnet', conversionPath?.leadMagnet],
        ['Follow up', conversionPath?.followUp],
      ]),
    },
    {
      title: 'Measurement',
      value: buildSectionValue([
        ['Primary event', measurement?.primaryEvent],
        ['Secondary events', measurement?.secondaryEvents || []],
        ['Optimization signal', measurement?.optimizationSignal],
      ]),
    },
    {
      title: 'Assumptions',
      value: assumptions.join(', '),
    },
  ];

  return sections.filter((section) => section.value);
};

const toCampaignBlueprintContent = (value: unknown): CampaignBlueprintContent => {
  const record = recordValue(value);
  const targetAudience = normalizeTargetAudience(record.targetAudience);
  const promotionContent = normalizePromotionContent(record.promotionContent);
  const conversionPath = normalizeConversionPath(record.conversionPath);
  const measurement = normalizeMeasurement(record.measurement);
  const assumptions = getTextList(record.assumptions);
  const explicitMetrics =
    normalizeMetrics(record.metrics ?? record.dims ?? record.dimensions ?? record.highlights) || [];
  const explicitSections = normalizeSections(record.sections ?? record.details);
  const explicitTags = getTextList(record.tags ?? record.labels ?? record.themes);
  const schemaMetrics = buildSchemaMetrics(record);
  const schemaSections = buildSchemaSections(record);
  const schemaTags = buildSchemaTags(record);

  return {
    ...record,
    title: textValue(record.title) || undefined,
    summary: textValue(record.summary) || textValue(record.description) || undefined,
    status: textValue(record.status) || undefined,
    kicker: textValue(record.kicker) || undefined,
    primaryGoal: textValue(record.primaryGoal) || undefined,
    audience: textValue(record.audience) || undefined,
    offer: textValue(record.offer) || undefined,
    channelMix: getTextList(record.channelMix),
    targetAudience,
    promotionContent,
    conversionPath,
    measurement,
    assumptions,
    metrics:
      explicitMetrics.length > 0
        ? explicitMetrics
        : schemaMetrics.length > 0
          ? schemaMetrics
          : buildFallbackMetrics(record),
    tags: explicitTags.length > 0 ? explicitTags : schemaTags,
    sections: explicitSections.length > 0 ? explicitSections : schemaSections,
  };
};

const getBlueprintTitle = (content: CampaignBlueprintContent, index: number) =>
  content.title || `Direction ${String.fromCharCode(65 + index)}`;

const getBlueprintSummary = (content: CampaignBlueprintContent) =>
  content.summary ||
  textValue(content.description) ||
  content.promotionContent?.coreMessage ||
  content.promotionContent?.landingPageAngle ||
  normalizeSections(content.sections)[0]?.value ||
  'A strategic landing page direction tailored to the current goal.';

const getBlueprintKicker = (content: CampaignBlueprintContent, index: number) =>
  content.kicker || `Direction ${String.fromCharCode(65 + index)}`;

const getBlueprintStatus = (content: CampaignBlueprintContent) => content.status || 'Ready';

const toCreationCampaignBlueprint = (
  blueprint: Partial<SiteCampaignBlueprint> & {
    contentJson?: string;
    content?: CampaignBlueprintContent;
    localId?: string;
  },
  index: number,
): CreationCampaignBlueprint => {
  const parsedContent =
    blueprint.content ||
    toCampaignBlueprintContent(
      blueprint.contentJson
        ? (parseJsonLike(blueprint.contentJson) ?? {})
        : ({} as Record<string, unknown>),
    );
  const contentJson = blueprint.contentJson || JSON.stringify(parsedContent);

  return {
    localId: blueprint.localId || String(blueprint.id || `blueprint-${index + 1}`),
    id: blueprint.id,
    siteId: blueprint.siteId,
    title: blueprint.title || getBlueprintTitle(parsedContent, index),
    summary: blueprint.summary || getBlueprintSummary(parsedContent),
    status: blueprint.status || getBlueprintStatus(parsedContent),
    kicker: getBlueprintKicker(parsedContent, index),
    metrics: parsedContent.metrics || [],
    tags: parsedContent.tags || [],
    content: parsedContent,
    contentJson,
    metaJson: blueprint.metaJson,
    sourceTaskCode: blueprint.sourceTaskCode,
    contentSchema: blueprint.contentSchema,
    schemaVersion: blueprint.schemaVersion,
    sortOrder: blueprint.sortOrder,
    linkedLandingPageCount: blueprint.linkedLandingPageCount,
    linkedAdCampaignCount: blueprint.linkedAdCampaignCount,
    linkedItemCount: blueprint.linkedItemCount,
    createTime: blueprint.createTime,
    modifyTime: blueprint.modifyTime,
  };
};

const extractBlueprintItems = (parsed: unknown): Record<string, unknown>[] => {
  if (Array.isArray(parsed)) {
    return parsed.filter((item): item is Record<string, unknown> =>
      Boolean(item && typeof item === 'object'),
    );
  }

  if (!parsed || typeof parsed !== 'object') return [];
  const record = parsed as Record<string, unknown>;
  const candidates = [
    record.blueprints,
    record.items,
    record.list,
    record.plans,
    record.data,
    record.result,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate.filter((item): item is Record<string, unknown> =>
        Boolean(item && typeof item === 'object'),
      );
    }
    if (candidate && typeof candidate === 'object') {
      const nested = candidate as Record<string, unknown>;
      if (Array.isArray(nested.blueprints)) {
        return nested.blueprints.filter((item): item is Record<string, unknown> =>
          Boolean(item && typeof item === 'object'),
        );
      }
      if (Array.isArray(nested.list)) {
        return nested.list.filter((item): item is Record<string, unknown> =>
          Boolean(item && typeof item === 'object'),
        );
      }
    }
  }

  return [record];
};

export const parseGeneratedCampaignBlueprints = (value: string): CreationCampaignBlueprint[] => {
  const parsed = parseJsonLike(value);
  const items = extractBlueprintItems(parsed);

  return items
    .map((item, index) => {
      const content = toCampaignBlueprintContent(item);
      return toCreationCampaignBlueprint(
        {
          localId: `generated-${index + 1}`,
          title: getBlueprintTitle(content, index),
          summary: getBlueprintSummary(content),
          status: getBlueprintStatus(content),
          sourceTaskCode: CAMPAIGN_BLUEPRINT_TASK_CODE,
          schemaVersion:
            numberValue(item.schemaVersion) ||
            numberValue((parsed as Record<string, unknown> | null)?.schemaVersion) ||
            DEFAULT_BLUEPRINT_SCHEMA_VERSION,
          contentJson: JSON.stringify(item),
          content,
        },
        index,
      );
    })
    .filter((item) => item.title && item.summary);
};

const truncateContext = (value: string, maxLength: number) => {
  const normalized = value.trim().replace(/\s+/g, ' ');
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength - 3).trim()}...`;
};

const buildBrandPdfSummaryLine = (brandPdfParsedContent: ParsedFileContentJson) => {
  const sectionCount =
    brandPdfParsedContent.summary.pageCount || brandPdfParsedContent.summary.sectionCount;
  return `${brandPdfParsedContent.summary.wordCount} words extracted across ${sectionCount} sections.`;
};

export const buildBlueprintGenerationParams = ({
  briefInput,
  websiteUrl,
  brandPdfName,
  brandPdfParsedContent,
}: {
  briefInput: string;
  websiteUrl?: string;
  brandPdfName?: string;
  brandPdfParsedContent?: ParsedFileContentJson | null;
}) => {
  const params: Record<string, unknown> = {
    briefInput,
  };
  const normalizedWebsiteUrl = websiteUrl?.trim();
  const normalizedBrandPdfName = brandPdfName?.trim() || brandPdfParsedContent?.fileName?.trim();

  if (normalizedWebsiteUrl) {
    params.websiteUrl = normalizedWebsiteUrl;
  }

  if (normalizedBrandPdfName) {
    params.brandPdfName = normalizedBrandPdfName;
  }

  if (brandPdfParsedContent) {
    params.brandPdf = [
      `File name: ${brandPdfParsedContent.fileName}`,
      `Summary: ${buildBrandPdfSummaryLine(brandPdfParsedContent)}`,
      `Excerpt: ${truncateContext(brandPdfParsedContent.normalizedText, 1200)}`,
    ].join('\n');
  }

  return params;
};

export const buildBlueprintGenerationPrompt = ({
  briefInput,
  websiteUrl,
  brandPdfParsedContent,
}: {
  briefInput: string;
  websiteUrl?: string;
  brandPdfParsedContent?: ParsedFileContentJson | null;
}) => {
  const lines = [
    briefInput.trim(),
    '',
    'Generate 3 distinct campaign blueprints for this landing page request.',
    'Return JSON only. Do not wrap the response in markdown code fences.',
    'Use exactly this top-level shape:',
    '{',
    '  "schemaCode": "campaign_blueprint",',
    '  "schemaVersion": 1,',
    '  "blueprints": [',
    '    {',
    '      "status": "Ready for execution",',
    '      "title": "short campaign blueprint title",',
    '      "summary": "1-2 sentence strategic summary",',
    '      "primaryGoal": "Trial signups",',
    '      "audience": "CTO / VP Eng",',
    '      "offer": "14-day team trial",',
    '      "channelMix": ["Search", "Retargeting"],',
    '      "targetAudience": {',
    '        "segments": ["Engineering leaders", "Platform teams"],',
    '        "painPoints": ["Slow debugging", "Release risk"],',
    '        "buyingTrigger": "Incident load or productivity review"',
    '      },',
    '      "promotionContent": {',
    '        "coreMessage": "Ship faster with AI-assisted debugging and release visibility",',
    '        "landingPageAngle": "Quantified time savings, proof, security readiness",',
    '        "googleAdsThemes": ["Developer productivity", "Debugging tools", "Release automation"]',
    '      },',
    '      "conversionPath": {',
    '        "landingPageCta": "Start free team trial",',
    '        "leadMagnet": "Security overview one-pager",',
    '        "followUp": "ROI calculator and implementation checklist"',
    '      },',
    '      "measurement": {',
    '        "primaryEvent": "Trial signup",',
    '        "secondaryEvents": ["Pricing view", "Security PDF download"],',
    '        "optimizationSignal": "Qualified demo request rate"',
    '      },',
    '      "assumptions": ["Use conservative planning language when source evidence is incomplete"]',
    '    }',
    '  ]',
    '}',
    'Every blueprint must include all fields shown above.',
  ];

  if (websiteUrl?.trim()) {
    lines.push('', `Business URL: ${websiteUrl.trim()}`);
  }

  if (brandPdfParsedContent) {
    lines.push(
      '',
      `Brand PDF: ${brandPdfParsedContent.fileName}`,
      `Brand PDF summary: ${buildBrandPdfSummaryLine(brandPdfParsedContent)}`,
      `Brand PDF excerpt: ${truncateContext(brandPdfParsedContent.normalizedText, 1200)}`,
    );
  }

  return lines.join('\n');
};

export const buildBlueprintBriefSection = (blueprint: CreationCampaignBlueprint) => {
  const lines = [`Blueprint title: ${blueprint.title}`, `Blueprint summary: ${blueprint.summary}`];

  if (blueprint.status.trim()) {
    lines.push(`Blueprint status: ${blueprint.status}`);
  }

  blueprint.metrics.forEach((metric) => {
    lines.push(`Blueprint ${metric.label}: ${metric.value}`);
  });

  if (blueprint.tags.length > 0) {
    lines.push(`Blueprint tags: ${blueprint.tags.join(', ')}`);
  }

  blueprint.content.sections?.forEach((section) => {
    if (!section.title.trim() || !section.value.trim()) return;
    lines.push('', `Blueprint section: ${section.title}`, section.value);
  });

  lines.push('');
  return lines.join('\n');
};

export const buildBriefPromptFromBlueprint = ({
  briefInput,
  blueprint,
  websiteUrl,
  websiteAnalysis,
  brandPdfParsedContent,
}: {
  briefInput: string;
  blueprint: CreationCampaignBlueprint;
  websiteUrl?: string;
  websiteAnalysis?: string;
  brandPdfParsedContent?: ParsedFileContentJson | null;
}) => {
  const lines = [
    'You will receive multiple labeled context blocks for one landing page brief task.',
    'Treat "Original landing page request" as the source of product facts, business constraints, audience, and non-negotiable goals.',
    'Treat "Selected campaign blueprint" as the strategic campaign direction that should shape positioning, messaging, CTA framing, proof direction, and measurement.',
    'If the two sources overlap, preserve the original product facts and use the selected campaign blueprint to sharpen the strategic angle.',
    'Use the campaign blueprint as internal planning context only. Do not reproduce blueprint labels, blueprint section names, or raw blueprint blocks in the final answer.',
    'Do not output sections named "Blueprint Direction", "Campaign setup", "Promotion content", "Conversion path", "Measurement", or "Assumptions" unless the task prompt explicitly requires them.',
    'For every single-choice section, output one option per line using markdown list syntax. Mark exactly one option with "- [checked]". Never combine multiple options into a paragraph.',
    'Output only the final landing page brief sections required by the task prompt. Do not echo the source context blocks.',
    '',
    'Original landing page request:',
    briefInput.trim(),
    '',
    'Selected campaign blueprint:',
    buildBlueprintBriefSection(blueprint).trim(),
  ];

  if (websiteUrl?.trim()) {
    lines.push('', 'Reference website URL:', websiteUrl.trim());
  }

  if (websiteAnalysis?.trim()) {
    lines.push('', 'Website analysis:', websiteAnalysis.trim());
  }

  if (brandPdfParsedContent) {
    lines.push(
      '',
      'Brand PDF reference:',
      `File name: ${brandPdfParsedContent.fileName}`,
      `Excerpt: ${truncateContext(brandPdfParsedContent.normalizedText, 1400)}`,
    );
  }

  return lines.join('\n');
};

export const toCreationCampaignBlueprintsFromSite = (items: SiteCampaignBlueprint[]) =>
  items.map((item, index) => toCreationCampaignBlueprint(item, index));
